import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { auth } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MultiSelect from "@/components/MultiSelect";
import { Download, Filter, ClipboardList, CheckCircle2, BarChart3, RefreshCw, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import ComplianceReport from "@/pages/ComplianceReport";

const PAGE_SIZE_DEFAULT = 50;

const TABS = [
  { v: "all",        label: "All", rowStatus: "" },
  { v: "assigned",   label: "All Assigned (Pending)", rowStatus: "Pending" },
  { v: "completed",  label: "All Completed", rowStatus: "Completed" },
  { v: "compliance", label: "Compliance", rowStatus: null }, // custom view
];

export default function Reports() {
  const [filters, setFilters] = useState({
    questionnaire_id: [], department_id: [], sub_department_id: [], category_id: [],
    date_from: "", date_to: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [masters, setMasters] = useState({ quizzes: [], depts: [], subs: [], cats: [] });

  async function loadMasters() {
    const [q, d, s, c] = await Promise.all([
      api.get("/questionnaires"),
      api.get("/masters/departments"),
      api.get("/masters/sub-departments"),
      api.get("/masters/categories"),
    ]);
    setMasters({ quizzes: q.data, depts: d.data, subs: s.data, cats: c.data });
  }

  async function loadReport() {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      Object.keys(filters).forEach(k => {
        const v = filters[k];
        if (Array.isArray(v)) { if (v.length) params[k] = v.join(","); }
        else if (v) { params[k] = v; }
      });
      const rs = TABS.find(t => t.v === tab)?.rowStatus;
      if (rs) params.row_status = rs;
      const r = await api.get("/reports/summary", { params });
      setData(r.data);
    } catch {
      toast.error("Failed to load report");
    } finally { setLoading(false); }
  }

  useEffect(() => { loadMasters(); }, []);
  useEffect(() => {
    // The Compliance tab renders its own component with independent filters/data; skip the summary loader.
    if (tab === "compliance") return;
    loadReport();
  }, [tab, page, pageSize]);

  // When filters change, reset to page 1 and refetch
  function applyFilters() { setPage(1); loadReport(); }

  async function exportXlsx(kind) {
    const params = new URLSearchParams({ kind });
    Object.keys(filters).forEach(k => {
      const v = filters[k];
      if (Array.isArray(v)) { if (v.length) params.set(k, v.join(",")); }
      else if (v) { params.set(k, v); }
    });
    const token = auth.token();
    try {
      const resp = await fetch(`${API}/reports/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("export failed");
      const blob = await resp.blob();
      const filename = resp.headers.get("Content-Disposition")?.match(/filename="(.+?)"/)?.[1] || `report-${kind}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind} report downloaded`);
    } catch (e) {
      toast.error("Export failed");
    }
  }

  const rows = data?.rows || [];
  const totalRowsForTab = data?.total ?? 0;

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Insights</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Training Reports</h1>
          <p className="text-slate-500 mt-1">Assigned vs Completed with detailed scoring — exportable to Excel.</p>
        </div>
        <Button onClick={loadReport} variant="outline" className="rounded-xl" disabled={loading} data-testid="refresh-report">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`}/>Refresh
        </Button>
      </header>

      {tab !== "compliance" && (
        <>
          <Card className="border-slate-200 rounded-2xl">
            <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-sky-600"/>
            <div className="font-bold text-sm">Filters</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <FilterMulti label="Quiz / Training" val={filters.questionnaire_id} onChange={v=>setFilters({...filters, questionnaire_id: v})} options={masters.quizzes} labelKey="title" tid="filter-quiz"/>
            <FilterMulti label="Department" val={filters.department_id} onChange={v=>setFilters({...filters, department_id: v, sub_department_id: []})} options={masters.depts} tid="filter-dept"/>
            <FilterMulti label="Sub-Department" val={filters.sub_department_id} onChange={v=>setFilters({...filters, sub_department_id: v})}
              options={masters.subs.filter(s => !filters.department_id.length || filters.department_id.includes(s.parent_id))} tid="filter-sub"/>
            <FilterMulti label="Category" val={filters.category_id} onChange={v=>setFilters({...filters, category_id: v})} options={masters.cats} tid="filter-category"/>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">From</Label>
              <Input type="date" value={filters.date_from} onChange={(e)=>setFilters({...filters, date_from: e.target.value})} data-testid="filter-from"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">To</Label>
              <Input type="date" value={filters.date_to} onChange={(e)=>setFilters({...filters, date_to: e.target.value})} data-testid="filter-to"/>
            </div>
            <div className="flex items-end gap-2 lg:col-span-2">
              <Button onClick={applyFilters} className="bg-sky-500 hover:bg-sky-600 flex-1" disabled={loading} data-testid="apply-filters">Apply</Button>
              <Button onClick={()=>{ setFilters({questionnaire_id:[], department_id:[], sub_department_id:[], category_id:[], date_from:"", date_to:""}); setPage(1); }} variant="outline">Clear</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI strip — totals computed server-side over the full matching set (independent of tab/page) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi icon={ClipboardList} label="Total Assigned" value={data?.totals?.assigned ?? 0} color="sky" tid="kpi-total-assigned"/>
        <Kpi icon={CheckCircle2} label="Completed" value={data?.totals?.completed ?? 0} color="emerald" tid="kpi-total-completed"/>
        <Kpi icon={ClipboardList} label="Pending" value={data?.totals?.pending ?? 0} color="amber" tid="kpi-total-pending"/>
        <Kpi icon={BarChart3} label="Completion Rate" value={`${data?.totals?.completion_rate ?? 0}%`} color="rose" tid="kpi-completion-rate"/>
        <Kpi icon={BarChart3} label="Avg Score" value={`${data?.totals?.avg_score_pct ?? 0}%`} color="indigo" tid="kpi-avg-score"/>
      </div>
        </>
      )}

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setPage(1); }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList className="bg-slate-100">
            {TABS.map(t => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}>
                {t.v === "compliance" && <ShieldCheck className="h-3.5 w-3.5 mr-1 inline"/>}
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tab !== "compliance" && (
            <div className="flex gap-2">
              <Button onClick={()=>exportXlsx("all")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-all"><Download className="h-4 w-4 mr-1.5"/>Export All</Button>
              <Button onClick={()=>exportXlsx("assigned")} variant="outline" data-testid="export-assigned"><Download className="h-4 w-4 mr-1.5"/>Assigned</Button>
              <Button onClick={()=>exportXlsx("completed")} variant="outline" data-testid="export-completed"><Download className="h-4 w-4 mr-1.5"/>Completed</Button>
            </div>
          )}
        </div>

        {tab === "compliance" ? (
          <TabsContent value="compliance" className="mt-4">
            <ComplianceReport/>
          </TabsContent>
        ) : (
          <TabsContent value={tab} className="mt-4">
            <ReportTable rows={rows} kind={tab} total={totalRowsForTab}
              page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} loading={loading}/>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function FilterMulti({ label, val, onChange, options, valueKey = "id", labelKey = "name", tid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      <MultiSelect
        value={val || []}
        onChange={onChange}
        options={options}
        valueKey={valueKey}
        labelKey={labelKey}
        placeholder={`All ${label.toLowerCase()}s`}
        testid={tid || `filter-${label.toLowerCase().replace(/\W+/g, "-")}`}
        chipsLabel="selected"
      />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color, tid }) {
  const map = {
    sky: "bg-sky-50 text-sky-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
    indigo: "bg-indigo-50 text-indigo-600",
  };
  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-4">
        <div className={`h-9 w-9 rounded-lg grid place-items-center ${map[color]}`}><Icon className="h-4.5 w-4.5"/></div>
        <div className="mt-2 font-display font-black text-2xl text-slate-900" data-testid={tid}>{value}</div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      </CardContent>
    </Card>
  );
}

function ReportTable({ rows, kind, total, page, setPage, pageSize, setPageSize, loading }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (!total && !loading) {
    return <Card className="border-slate-200 rounded-2xl"><CardContent className="p-10 text-center text-slate-400">No data for current filters.</CardContent></Card>;
  }
  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`report-table-${kind}`}>
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="py-3 px-4">Emp ID</th>
                <th className="py-3 px-4">Employee</th>
                <th className="py-3 px-4">Dept / Sub / Category</th>
                <th className="py-3 px-4">Quiz · Type</th>
                <th className="py-3 px-4 text-center">Qs</th>
                <th className="py-3 px-4 text-center">✓ / ✗</th>
                <th className="py-3 px-4">Best %</th>
                <th className="py-3 px-4 whitespace-nowrap">Pre / Post</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Completed At</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                  <td className="py-3 px-4 font-mono font-bold text-sky-700">{r.emp_id}</td>
                  <td className="py-3 px-4"><div className="font-medium">{r.emp_name}</div><div className="text-xs text-slate-400">{r.designation || r.email}</div></td>
                  <td className="py-3 px-4 text-xs text-slate-600">{r.department || "—"}<br/><span className="text-slate-400">{r.sub_department || ""} {r.category ? "· " + r.category : ""}</span></td>
                  <td className="py-3 px-4"><div className="font-medium">{r.quiz_title}</div>{r.training_type && <div className="text-[11px] text-slate-400">{r.training_type}</div>}</td>
                  <td className="py-3 px-4 text-center text-xs">{r.total_questions || 0}</td>
                  <td className="py-3 px-4 text-center text-xs">
                    <span className="text-green-700 font-bold">{r.correct_answers || 0}</span>
                    <span className="text-slate-300"> / </span>
                    <span className="text-red-700">{r.wrong_answers || 0}</span>
                  </td>
                  <td className="py-3 px-4">
                    {r.best_pct != null ? (
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                        r.best_pct >= 85 ? "bg-green-100 text-green-700" :
                        r.best_pct >= 70 ? "bg-sky-100 text-sky-700" :
                        r.best_pct >= 50 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"
                      }`}>{r.best_pct}%</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="py-3 px-4 text-xs whitespace-nowrap">
                    <span className={r.pre_score_pct != null ? "text-violet-700 font-bold" : "text-slate-300"}>{r.pre_score_pct != null ? `${r.pre_score_pct}%` : "—"}</span>
                    <span className="text-slate-300"> / </span>
                    <span className={r.post_score_pct != null ? "text-emerald-700 font-bold" : "text-slate-300"}>{r.post_score_pct != null ? `${r.post_score_pct}%` : "—"}</span>
                    {r.is_late_post && (
                      <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold uppercase tracking-wider" title="Post-Test taken after the window expired" data-testid={`late-badge-${r.emp_id}-${r.quiz_id}`}>
                        <Clock className="h-2.5 w-2.5"/>Late
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <Badge variant="secondary" className={r.status === "Completed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>{r.status}</Badge>
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-500">{r.completed_at ? new Date(r.completed_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
          <div>
            Showing {total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(total, page * pageSize)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <span>Page size</span>
            <select value={pageSize} onChange={(e)=>{ setPageSize(parseInt(e.target.value)); setPage(1); }}
              className="border border-slate-200 rounded px-2 py-1" data-testid={`page-size-${kind}`}>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={()=>setPage(p=>p-1)} className="h-7" data-testid={`prev-${kind}`}>Prev</Button>
            <span className="font-mono">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={()=>setPage(p=>p+1)} className="h-7" data-testid={`next-${kind}`}>Next</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
